/**
 * Money and weight arrive from the API as strings and stay strings. Parsing
 * them into JavaScript numbers to format them would reintroduce exactly the
 * float error the backend works so hard to avoid.
 */

const groupIndian = (whole: string): string => {
  if (whole.length <= 3) return whole;
  const last3 = whole.slice(-3);
  const rest = whole.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
};

export function money(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const negative = value.startsWith('-');
  const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');
  return `${negative ? '−' : ''}₹${groupIndian(whole)}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}

export function weight(value: string | null | undefined, dp = 3): string {
  if (value === null || value === undefined || value === '') return '—';
  const [whole = '0', fraction = ''] = value.split('.');
  return `${whole}.${fraction.padEnd(dp, '0').slice(0, dp)} g`;
}

export const fine = (value: string | null | undefined): string => weight(value, 4);

export function date(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Today on the shop's own clock — toISOString would give yesterday before 5:30 am in India. */
export const today = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * An address as one readable line.
 *
 * Addresses are stored as their parts — line1, city, state, pincode — because
 * that is how they are searched and filed. A printed document wants a sentence,
 * and handing the stored object straight to React empties the screen with
 * "objects are not valid as a React child", which is exactly what the estimate
 * print page did. Anything that is already a string passes through untouched.
 */
export function addressLine(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  // A number or a boolean somehow stored as an address: say what it is rather
  // than printing "[object Object]".
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value !== 'object') return '';

  const a = value as Record<string, unknown>;
  const parts = ['line1', 'line2', 'street', 'area', 'city', 'district', 'state', 'pincode', 'pin', 'country']
    .map((k) => a[k])
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '');

  // An address shape nobody anticipated still reads as something rather than
  // breaking the page.
  if (parts.length === 0) {
    return Object.values(a)
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      .join(', ');
  }
  return parts.join(', ');
}

/** A rate per gram as money — never the raw "7478.0000" the database keeps. */
export const rate = (value: string | null | undefined): string => money(value);

const TESTING_WORDS: Record<string, string> = {
  TOUCHSTONE: 'touchstone',
  XRF: 'XRF machine',
  CARAT_METER: 'carat meter',
  FIRE_ASSAY: 'fire assay',
};

/** How purity was tested, as a sentence fragment: "XRF machine", "touchstone". */
export function testingWords(method: string | null | undefined): string {
  if (!method) return 'testing';
  return TESTING_WORDS[method] ?? method.replace(/_/g, ' ').toLowerCase();
}

const PAY_WORDS: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  BANK: 'Bank / UPI',
  CHEQUE: 'Cheque',
  AGAINST_SALE: 'Kept as credit for a new purchase',
  ADVANCE: 'From advance',
  KEPT_AS_ADVANCE: 'Kept as advance with the shop',
  EXCHANGE: 'Old gold exchange',
  CREDIT: 'On credit',
  NEFT: 'NEFT',
  RTGS: 'RTGS',
};

/** A payment mode the way a customer reads it: "Cash", not "CASH". */
export function payWords(mode: string | null | undefined): string {
  if (!mode) return '';
  return PAY_WORDS[mode] ?? mode.charAt(0) + mode.slice(1).toLowerCase().replace(/_/g, ' ');
}

/** "1 instalment", "3 instalments". */
export const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;
